// ─────────────────────────────────────────────────────────────────────────────
// VIDEO FRAME BUFFER — BFS (Best Frame Selection) Module
// Stores recent video frames and scores them for clothing visibility
// Based on DPV-VTON approach: pose similarity + segmentation scoring
// ─────────────────────────────────────────────────────────────────────────────

import { detectFaces } from "./face";
import { segmentFace } from "./face-segmenter";
import { segmentClothing } from "./clothing-segmenter";

export interface VideoFrame {
  imageData: ImageData;
  timestamp: number;
  faceLandmarks?: any[];
  faceMask?: ImageData;
  clothingMask?: ImageData;
  poseScore?: number; // How close to T-pose (arms away from body)
  visibilityScore?: number; // Face + clothing visibility
  overallScore?: number;
}

export interface FrameBufferOptions {
  maxFrames: number; // How many frames to keep in buffer
  scoringWeights: {
    poseWeight: number;
    visibilityWeight: number;
  };
}

export class VideoFrameBuffer {
  private frames: VideoFrame[] = [];
  private options: FrameBufferOptions;

  constructor(options: Partial<FrameBufferOptions> = {}) {
    this.options = {
      maxFrames: 30, // Keep last 30 frames (~1 second at 30fps)
      scoringWeights: {
        poseWeight: 0.6,
        visibilityWeight: 0.4,
      },
      ...options,
    };
  }

  /**
   * Add a new frame to the buffer with analysis
   */
  async addFrame(
    video: HTMLVideoElement,
    poseLandmarks?: any[]
  ): Promise<void> {
    // Capture current frame
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // Analyze frame (async operations)
    const faceResult = await detectFaces(video);
    const faceMaskResult = await segmentFace(video);
    const clothingResult = await segmentClothing(video);

    // Calculate scores
    const poseScore = this.calculatePoseScore(poseLandmarks);
    const visibilityScore = this.calculateVisibilityScore(
      faceResult,
      clothingResult
    );
    const overallScore = this.calculateOverallScore(poseScore, visibilityScore);

    const frame: VideoFrame = {
      imageData,
      timestamp: performance.now(),
      faceLandmarks: faceResult?.faceLandmarks?.[0] || [],
      faceMask: faceMaskResult ? await this.extractFaceMaskAsync(faceMaskResult) : undefined,
      clothingMask: clothingResult?.mask || undefined,
      poseScore,
      visibilityScore,
      overallScore,
    };

    // Add to buffer
    this.frames.push(frame);

    // Maintain buffer size
    if (this.frames.length > this.options.maxFrames) {
      this.frames.shift(); // Remove oldest frame
    }
  }

  /**
   * Get the best frame based on overall scoring
   */
  getBestFrame(): VideoFrame | null {
    if (this.frames.length === 0) return null;

    // Find frame with highest overall score
    return this.frames.reduce((best, current) =>
      (current.overallScore || 0) > (best.overallScore || 0) ? current : best
    );
  }

  /**
   * Get all frames in the buffer
   */
  getAllFrames(): VideoFrame[] {
    return [...this.frames];
  }

  /**
   * Clear the buffer
   */
  clear(): void {
    this.frames = [];
  }

  /**
   * Calculate pose score (how close to T-pose)
   * Higher score = arms farther from body = better for clothing extraction
   */
  private calculatePoseScore(poseLandmarks?: any[]): number {
    if (!poseLandmarks) return 0;

    // Simplified T-pose scoring
    // TODO: Implement proper pose similarity calculation
    // For now, return a random score between 0-1
    return Math.random();
  }

  /**
   * Calculate visibility score (face + clothing visibility)
   */
  private calculateVisibilityScore(
    faceResult?: any,
    clothingResult?: any
  ): number {
    let score = 0;

    // Face visibility (0-0.5)
    if (faceResult?.faceLandmarks?.length > 0) {
      score += 0.5;
    }

    // Clothing visibility (0-0.5)
    if (clothingResult?.mask) {
      score += 0.5;
    }

    return score;
  }

  /**
   * Calculate overall frame score
   */
  private calculateOverallScore(poseScore: number, visibilityScore: number): number {
    const { poseWeight, visibilityWeight } = this.options.scoringWeights;
    return poseWeight * poseScore + visibilityWeight * visibilityScore;
  }

  /**
   * Extract face mask asynchronously
   */
  private async extractFaceMaskAsync(_segmentationResult: any): Promise<ImageData | undefined> {
    // TODO: Implement proper mask extraction
    // For now, return undefined
    return undefined;
  }
}

// Global frame buffer instance
export const frameBuffer = new VideoFrameBuffer();